// 資訊頁賠付表 — 對照 Unity `Slot026_RedRidingHood/Prefab/UI/GameRuleInfo.prefab` 的 `Symbols` 節點。
//
// 🎯 **這是本作賠付表數值的權威來源**。長期以來 `Data/PayTableData.asset` 的 `SymbolOdds` 是空陣列，
//    因此本移植的 `game/config/payTable.ts` 只能放 MOCK 值；實際上 Unity 把賠付數字**硬編在這份 prefab 的
//    TMP 文字裡**（GameRuleInfo 完全沒有掛任何 Script，純靜態版面），故此處為逐字抄錄的真實數值。
//
// 版面（Unity 設計 px）：
//   Symbols 節點 1000×981，GridLayoutGroup cellSize {250, 327}、spacing 0、constraint=FixedColumnCount(4)、
//   childAlignment MiddleCenter、startCorner UpperLeft、startAxis Horizontal ⇒ 11 格排成 4 / 4 / 3。
//   格內 VerticalLayoutGroup：前 4 格（H1~H4，符號 214×192）MiddleCenter、spacing 0；
//   後 7 格（H5~LQ，符號 214×134）LowerCenter、spacing 23。
//   兩者的 childControlHeight/childForceExpandHeight 皆為 0 ⇒ VLG **不改子節點高度**，只依各自
//   rect 高度排列：上排 192+0+135=327（剛好填滿格高）、下排 134+23+135=292（貼齊格底、上方留 35）。
//   ScoreText 節點 130×135、fontSize 24、lineSpacing −5、純白（無漸層）、overflowMode=Overflow。
//   ⚠️ 對齊方式在來源即不一致：H1／H2 兩格是 Left+Top、其餘 9 格是 Center+Middle。此處照抄不「順手修正」。
//
// ⚠️ **抄錄陷阱**：prefab 的 `m_text` 在 YAML 裡是單引號多行純量，各列之間看起來隔著空行，
//    但 YAML 的 flow scalar folding 會把「空行」折成**一個**換行、並吃掉續行縮排。
//    也就是說真實字串是單一 `\n` 分隔，不是空行分隔——照字面抄成 `\n\n` 會讓行距變兩倍而撞到隔壁格。
//    交叉驗證：H1 共 5 列 × 行高 26.88 = 134.4 ≈ ScoreText 的 rect 高 135，剛好吻合。

import { Container, Sprite, Text } from 'pixi.js'
import { fontFamilyForLocale } from '../../../../framework/i18n/i18n'
import { getSymbolTexture } from '../../../assets/SymbolTextures'
import type { InfoBlock } from './infoBlocks'

const CELL_W = 250
const CELL_H = 327
const COLUMNS = 4
const SCORE_W = 130
/** ScoreText 的 rect 高（VLG 不改子高度，故排版一律用這個值，而非量測到的文字高）。 */
const SCORE_H = 135
const SCORE_FONT = 24
/** 行高／fontSize（已含 TMP lineSpacing −5 ＝ fontSize 的 −5%）：24 × 1.12 = 26.88。 */
const SCORE_LINE_RATIO = 1.12

interface PayCell {
  /** 符號 id（config/symbols.ts）。 */
  readonly symbolId: number
  readonly symbolW: number
  readonly symbolH: number
  /** VLG 對齊：H1~H4 為 Middle+spacing 0、其餘為 Lower+spacing 23。 */
  readonly bottomAligned: boolean
  /** ScoreText 對齊（來源不一致，見檔頭）。 */
  readonly align: 'left' | 'center'
  /** 「連線數 + 三個空格 + 倍數」逐列（來源以單一換行分隔，見檔頭的抄錄陷阱）。 */
  readonly rows: readonly string[]
}

/** 逐字抄自 GameRuleInfo.prefab 的 ScoreText（m_text）。 */
const PAY_CELLS: readonly PayCell[] = [
  { symbolId: 1, symbolW: 214, symbolH: 192, bottomAligned: false, align: 'left', rows: ['6   50.00', '5   20.00', '4   8.00', '3   4.00', '2   2.00'] },
  { symbolId: 2, symbolW: 214, symbolH: 192, bottomAligned: false, align: 'left', rows: ['6   6.00', '5   2.40', '4   1.20', '3   0.60'] },
  { symbolId: 3, symbolW: 214, symbolH: 192, bottomAligned: false, align: 'center', rows: ['6   5.00', '5   2.00', '4   1.00', '3   0.50'] },
  { symbolId: 4, symbolW: 214, symbolH: 192, bottomAligned: false, align: 'center', rows: ['6   4.00', '5   1.60', '4   0.80', '3   0.40'] },
  { symbolId: 5, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   2.40', '5   1.20', '4   0.60', '3   0.30'] },
  { symbolId: 6, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   1.20', '5   0.75', '4   0.50', '3   0.25'] },
  { symbolId: 7, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   1.00', '5   0.60', '4   0.40', '3   0.20'] },
  { symbolId: 8, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   0.50', '5   0.40', '4   0.25', '3   0.15'] },
  { symbolId: 11, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   0.40', '5   0.30', '4   0.20', '3   0.10'] },
  { symbolId: 9, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   0.40', '5   0.30', '4   0.20', '3   0.10'] },
  { symbolId: 10, symbolW: 214, symbolH: 134, bottomAligned: true, align: 'center', rows: ['6   0.40', '5   0.30', '4   0.20', '3   0.10'] },
]

/** 格內符號與文字的間距（Unity VLG spacing）。 */
const SPACING_TOP_ROW = 0
const SPACING_LOWER_ROW = 23

export function payTableBlock(): InfoBlock {
  const node = new Container()
  const cells = PAY_CELLS.map((cell) => {
    const sprite = new Sprite(getSymbolTexture(cell.symbolId, 1))
    sprite.anchor.set(0.5, 0)
    const score = new Text({
      text: cell.rows.join('\n'),
      style: {
        fontFamily: fontFamilyForLocale(),
        fontSize: SCORE_FONT,
        fill: 0xffffff,
        align: cell.align,
      },
    })
    // 來源的水平與垂直對齊是連動的：Left 的兩格同時是 Top、Center 的九格同時是 Middle。
    score.anchor.set(cell.align === 'center' ? 0.5 : 0, cell.align === 'center' ? 0.5 : 0)
    node.addChild(sprite, score)
    return { cell, sprite, score }
  })

  const rowCount = Math.ceil(PAY_CELLS.length / COLUMNS)

  return {
    node,
    layout(s, centerX, top) {
      const gridLeft = centerX - (CELL_W * COLUMNS * s) / 2
      cells.forEach(({ cell, sprite, score }, index) => {
        const col = index % COLUMNS
        const row = Math.floor(index / COLUMNS)
        const cellCX = gridLeft + (col + 0.5) * CELL_W * s
        const cellTop = top + row * CELL_H * s

        sprite.setSize(cell.symbolW * s, cell.symbolH * s)
        score.style.fontSize = SCORE_FONT * s
        score.style.lineHeight = SCORE_FONT * SCORE_LINE_RATIO * s

        const spacing = (cell.bottomAligned ? SPACING_LOWER_ROW : SPACING_TOP_ROW) * s
        // VLG 用各子節點自己的 rect 高排列（childControlHeight=0），故用 SCORE_H 而非量測到的文字高。
        const inner = (cell.symbolH + SCORE_H) * s + spacing
        // MiddleCenter 在格內垂直置中；LowerCenter 貼齊格底。
        const innerTop = cell.bottomAligned ? cellTop + CELL_H * s - inner : cellTop + (CELL_H * s - inner) / 2

        sprite.position.set(cellCX, innerTop)
        // 文字在自己的 130×135 rect 內對齊（Center 的九格垂直置中、Left 的兩格貼齊頂端）。
        const scoreTop = innerTop + cell.symbolH * s + spacing
        score.position.set(
          cell.align === 'center' ? cellCX : cellCX - (SCORE_W * s) / 2,
          cell.align === 'center' ? scoreTop + (SCORE_H * s) / 2 : scoreTop,
        )
      })
      return rowCount * CELL_H * s
    },
  }
}
